import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faPaperPlane, faTimes, faSpinner, faComments } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '@clerk/clerk-react';
import { useRevit } from '../../contexts/RevitContext';
import axios from 'axios';
import './AIChatbot.css';

const AIChatbot = ({ isVIPAccount, isOpen, setIsOpen, onOpen }) => {
    const [currentView, setCurrentView] = useState('menu'); // 'menu', 'chat', 'form'
    const [messages, setMessages] = useState([
        { 
            type: 'bot', 
            content: 'Hi there! I\'m your RevTrack AI assistant. You can ask me about using Revtrack, or you can report a problem. However, note that I am just an AI bot and may sometimes give incorrect advice.' 
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        type: 'bug',
        message: '',
        screenshot: null
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const messagesEndRef = useRef(null);
    const { getToken } = useAuth();
    const { username } = useRevit();
    const [developerMessages, setDeveloperMessages] = useState([
        { 
            type: 'bot', 
            content: 'Hi! I am Tuomas, Revtrack\'s co-founder & lead developer. Send your message and I\'ll get back to you as soon as possible.' 
        }
    ]);
    const [developerInputValue, setDeveloperInputValue] = useState('');
    const [isDeveloperLoading, setIsDeveloperLoading] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [isLoadingConversation, setIsLoadingConversation] = useState(false);
    const [aiConversationId, setAiConversationId] = useState(null);

    // Allow external components to open the chatbot via a custom event
    useEffect(() => {
        const handler = () => setIsOpen(true);
        window.addEventListener('open-ai-chatbot', handler);
        return () => window.removeEventListener('open-ai-chatbot', handler);
    }, []);

    const handleDeveloperInputChange = (e) => {
        setDeveloperInputValue(e.target.value);
    };

    const loadConversation = async () => {
        setIsLoadingConversation(true);
        try {
            const token = await getToken();
            
            // Get or create conversation
            const conversationResponse = await axios.get(`https://${SERVER_URL}/api/chat/conversation`, {
                params: { username },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            const convId = conversationResponse.data.conversationId;
            setConversationId(convId);
            
            // Load existing messages
            const messagesResponse = await axios.get(`https://${SERVER_URL}/api/chat/messages/${convId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            const messages = messagesResponse.data.messages;
            
            if (messages.length === 0) {
                // No existing messages, set default welcome message
                setDeveloperMessages([
                    { 
                        type: 'bot', 
                        content: 'Hi! I am Tuomas, Revtrack\'s co-founder & lead developer. Send your message and I\'ll get back to you as soon as possible.' 
                    }
                ]);
            } else {
                // Convert database messages to component format
                const formattedMessages = messages.map(msg => ({
                    type: msg.sender_type === 'developer' ? 'bot' : 'user',
                    content: msg.message_content,
                    id: msg.id,
                    timestamp: msg.timestamp
                }));
                setDeveloperMessages(formattedMessages);
            }
        } catch (error) {
            console.error('Error loading conversation:', error);
            setDeveloperMessages([
                { 
                    type: 'bot', 
                    content: 'Hi! I am Tuomas, Revtrack\'s co-founder & lead developer. Send your message and I\'ll get back to you as soon as possible.' 
                }
            ]);
        } finally {
            setIsLoadingConversation(false);
        }
    };

    const sendDeveloperMessage = async () => {
        if (developerInputValue.trim() === '' || !conversationId) return;
        
        const userMessage = { type: 'user', content: developerInputValue };
        setDeveloperMessages(prev => [...prev, userMessage]);
        setDeveloperInputValue('');
        
        setIsDeveloperLoading(true);
        
        try {
            const token = await getToken();
            await axios.post(`https://${SERVER_URL}/api/chat/send-message`, {
                conversationId,
                message: userMessage.content,
                senderType: 'user'
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            
        } catch (error) {
            console.error('Error sending message to developer:', error);
            setDeveloperMessages(prev => [...prev, { 
                type: 'bot', 
                content: 'Sorry, there was an error sending your message. Please try again.' 
            }]);
        } finally {
            setIsDeveloperLoading(false);
        }
    };

    const handleDeveloperKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendDeveloperMessage();
        }
    };
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;

    // Function to format the message content with markdown-like syntax
    const formatMessage = (content) => {
        if (!content) return '';
        
        // Replace headers (### Text) with bold styled text
        let formatted = content.replace(/###\s+(.*?)($|\n)/g, '<h3>$1</h3>$2');
        
        // Replace ## headers
        formatted = formatted.replace(/##\s+(.*?)($|\n)/g, '<h2>$1</h2>$2');
        
        // Replace # headers
        formatted = formatted.replace(/#\s+(.*?)($|\n)/g, '<h1>$1</h1>$2');
        
        // Replace bold text (**text**)
        formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        
        // Replace italic text (*text*)
        formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
        
        // Replace bullet points
        formatted = formatted.replace(/- (.*?)($|\n)/g, '<li>$1</li>$2');
        
        // Ensure paragraphs have proper spacing
        formatted = formatted.replace(/\n\n/g, '<br/><br/>');
        
        // Replace single newlines with line breaks
        formatted = formatted.replace(/\n/g, '<br/>');
        
        return formatted;
    };

    const toggleChat = () => {  
        if (!isOpen && onOpen) {
            onOpen();
        }
        setIsOpen(!isOpen);
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, developerMessages]);

    const sendMessage = async () => {
        if (inputValue.trim() === '') return;
        
        const userMessage = { type: 'user', content: inputValue };
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        
        setIsLoading(true);
        
        try {
            const token = await getToken();
            const response = await axios.post(`https://${SERVER_URL}/api/gpt-assistant-reply`, {
                message: userMessage.content,
                username: username,
                conversationId: aiConversationId
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            
            // Store the conversation ID for subsequent messages
            if (response.data.conversationId && !aiConversationId) {
                setAiConversationId(response.data.conversationId);
            }
            
            // Use the formatted response
            setMessages(prev => [...prev, { 
                type: 'bot', 
                content: response.data.response,
                formattedContent: formatMessage(response.data.response)
            }]);
        } catch (error) {
            console.error('Error sending message to AI:', error);
            setMessages(prev => [...prev, { 
                type: 'bot', 
                content: 'Sorry, I encountered an error. Please try again later.' 
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleMenuSelection = (view) => {
        setCurrentView(view);
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setFormData(prev => ({
                ...prev,
                screenshot: file
            }));
        } else if (file) {
            alert('Please select an image file');
            e.target.value = '';
        }
    };

    const submitForm = async () => {
        if (!formData.email || !formData.message) {
            alert('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);
        try {
            const token = await getToken();
            
            const formDataToSend = new FormData();
            formDataToSend.append('email', formData.email);
            formDataToSend.append('type', formData.type);
            formDataToSend.append('message', formData.message);
            formDataToSend.append('username', username);
            
            if (formData.screenshot) {
                formDataToSend.append('screenshot', formData.screenshot);
            }

            const response = await axios.post(`https://${SERVER_URL}/api/submit-feedback`, formDataToSend, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                alert('Thank you for your feedback! We\'ll get back to you soon.');
                setFormData({ email: '', type: 'bug', message: '', screenshot: null });
                setCurrentView('menu');
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
            alert('Error submitting feedback. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderMenuView = () => (
        <div className="chat-menu">
            {isVIPAccount ? (
                <>
                    <div className="menu-option" onClick={() => window.location.href = 'mailto:vip@getrevtrack.com'}>
                        <div className="menu-icon">📧</div>
                        <div className="menu-text">
                            <h4>Send Email</h4>
                            <p>Send an email to vip@getrevtrack.com</p>
                        </div>
                    </div>
                    <div className="menu-option" onClick={() => handleMenuSelection('chat')}>
                        <div className="menu-icon">👨‍💻</div>
                        <div className="menu-text">
                            <h4>Live Chat with Co-Founder</h4>
                            <p>Get direct support from our Co-Founder & Lead Developer</p>
                        </div>
                    </div>
                </>
            ) : (
                <>
                                    <div className="menu-option" onClick={() => window.open('https://getrevtrack.com/training/', '_blank')}>
                        <div className="menu-icon">🎓</div>
                        <div className="menu-text">
                            <h4>Video Tutorials</h4>
                            <p>Learn how to get the most out of RevTrack</p>
                        </div>
                    </div>
                    <div className="menu-option" onClick={() => handleMenuSelection('chat')}>
                        <div className="menu-icon">🤖</div>
                        <div className="menu-text">
                            <h4>Instant AI Support</h4>
                            <p>Get support from the Revtrack AI Chatbot</p>
                        </div>
                    </div>
                    <div className="menu-option" onClick={() => handleMenuSelection('form')}>
                        <div className="menu-icon">🐛</div>
                        <div className="menu-text">
                            <h4>Human support</h4>
                            <p>Send us feedback, bug reports, or feature requests. (We respond within 24 hours)</p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );

    const renderFormView = () => (
        <div className="feedback-form">
            <button className="back-button" onClick={() => setCurrentView('menu')}>
                ← Back to Menu
            </button>
            <h4>Send us your feedback</h4>
            <div className="form-group">
                <label>Email:</label>
                <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    placeholder="your@email.com"
                    required
                />
            </div>
            <div className="form-group">
                <label>Type:</label>
                <select
                    value={formData.type}
                    onChange={(e) => handleFormChange('type', e.target.value)}
                >
                    <option value="bug">Bug Report</option>
                    <option value="setup">Setup Issue</option>
                    <option value="idea">Feature Idea</option>
                    <option value="other">Other</option>
                </select>
                {formData.type === 'setup' && (
                    <p className="form-type-hint">
                        📌 Note: please send your landing page &amp; thank you page links alongside the message
                    </p>
                )}
            </div>
            <div className="form-group">
                <label>Message:</label>
                <textarea
                    value={formData.message}
                    onChange={(e) => handleFormChange('message', e.target.value)}
                    placeholder="Describe your issue or idea..."
                    rows={4}
                    required
                />
            </div>
            <div className="form-group">
                <label>Screenshot (optional):</label>
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                />
                {formData.screenshot && (
                    <div className="file-preview">
                        📷 {formData.screenshot.name}
                    </div>
                )}
            </div>
            <button 
                className="submit-button" 
                onClick={submitForm}
                disabled={isSubmitting || !formData.email || !formData.message}
            >
                {isSubmitting ? 'Sending...' : 'Send Feedback'}
            </button>
        </div>
    );

    const renderChatView = () => (
        <>
            <button className="back-button" onClick={() => setCurrentView('menu')}>
                ← Back to Menu
            </button>
            <div className="chat-messages">
                {messages.map((message, index) => (
                    <div key={index} className={`message ${message.type}`}>
                        {message.type === 'bot' && message.formattedContent ? (
                            <div dangerouslySetInnerHTML={{ __html: message.formattedContent }} />
                        ) : (
                            message.content
                        )}
                    </div>
                ))}
                {isLoading && (
                    <div className="message bot loading">
                        <FontAwesomeIcon icon={faSpinner} spin /> Thinking...
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            <div className="chat-input">
                <textarea
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your question here..."
                    disabled={isLoading}
                />
                <button 
                    className="send-button" 
                    onClick={sendMessage}
                    disabled={isLoading || inputValue.trim() === ''}
                >
                    <FontAwesomeIcon icon={faPaperPlane} />
                </button>
            </div>
        </>
    );

    const renderDeveloperChatView = () => (
        <>
            <div className="chat-messages">
                {developerMessages.map((message, index) => (
                    <div key={index} className={`message-wrapper ${message.type}`}>
                        {message.type === 'bot' && (
                            <img 
                                src="/IMC_icon.jpeg" 
                                alt="Developer" 
                                className="message-avatar"
                            />
                        )}
                        <div className={`message ${message.type}`}>
                            {message.content}
                        </div>
                        {message.type === 'user' && <span className="message-avatar">👤</span>}
                    </div>
                ))}
                {isDeveloperLoading && (
                    <div className="message-wrapper bot">
                        <span className="message-avatar">👨‍💻</span>
                        <div className="message bot loading">
                            <FontAwesomeIcon icon={faSpinner} spin /> Sending...
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            <div className="chat-input">
                <textarea
                    value={developerInputValue}
                    onChange={handleDeveloperInputChange}
                    onKeyPress={handleDeveloperKeyPress}
                    placeholder="Type your message to the developer..."
                    disabled={isDeveloperLoading}
                />
                <button 
                    className="send-button" 
                    onClick={sendDeveloperMessage}
                    disabled={isDeveloperLoading || developerInputValue.trim() === ''}
                >
                    <FontAwesomeIcon icon={faPaperPlane} />
                </button>
            </div>
        </>
    );

    // Load conversation when switching to developer chat
    useEffect(() => {
        if (currentView === 'chat' && isVIPAccount && username) {
            loadConversation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentView, isVIPAccount, username]);

    return (
        <div className={`ai-chatbot-container ${isVIPAccount ? 'vip-account' : ''}`}>
            {isOpen && (
                <div className="chat-window">
                    <div className="chat-header">
                        <div className="chat-title">
                            {!isVIPAccount && <FontAwesomeIcon icon={faRobot} />}
                            {isVIPAccount ? 'Message the Co-Founder' : 
                             currentView === 'menu' ? 'RevTrack Support' :
                             currentView === 'chat' ? 'AI Chatbot' :
                             'Send Feedback'}
                        </div>
                        <button className={`${isVIPAccount ? 'vip-close-button' : 'chat-close-button'}`} onClick={toggleChat}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    <div className="chat-content">
                        {currentView === 'menu' && renderMenuView()}
                        {currentView === 'form' && renderFormView()}
                        {currentView === 'chat' && isVIPAccount && (
                            isLoadingConversation ? (
                                <div className="loading-conversation">
                                    <FontAwesomeIcon icon={faSpinner} spin /> Loading chat...
                                </div>
                            ) : (
                                renderDeveloperChatView()
                            )
                        )}
                        {currentView === 'chat' && !isVIPAccount && renderChatView()}
                    </div>
                </div>
            )}
            <button 
                className={`chat-toggle-button ${isOpen ? 'open' : ''}`} 
                onClick={toggleChat}
            >
                <FontAwesomeIcon icon={faComments} />
                {!isOpen && <span>{isVIPAccount ? 'VIP Support' : 'Support'}</span>}
            </button>
        </div>
    );
};

export default AIChatbot;
